<script lang="ts">
	import { getTokenStorage } from '../utils/localStorage';
	import { appStore } from '../stores/appStore';
	import type { AppApiClient } from '../api_contracts/app.client';

	let isAuthenticated = $state(false);
	let userFirstName = $state('');
	let userLastName = $state('');
	let avatarUrl = $state('');
	let showUserMenu = $state(false);
	let client: AppApiClient | null = $state(null);
	let notificationCount = $state(5); // Mock notification count

	// Subscribe to store
	appStore.subscribe(state => {
		client = state.client;
	});

	// Check authentication status and load profile
	$effect(() => {
		const token = getTokenStorage();
		isAuthenticated = !!token;
		
		if (isAuthenticated && client) {
			loadUserProfile(token);
		}
	});
 
	async function loadUserProfile(token: string) {
		try {
			const result = await client?.getUserProfile({ token });
			if (result?.response.response.successFull) {
				userFirstName = result.response.profile.firstName || '';
				userLastName = result.response.profile.lastName || '';
				avatarUrl = result.response.profile.avatarUrl || '';
			}
		} catch (error) {
			console.error('Failed to load user profile:', error);
		}
	}

	function handleSignOut() {
		localStorage.removeItem('koru_token');
		localStorage.removeItem('token');
		appStore.reset();
		window.location.hash = '/login';
	}

	function navigateToNotifications() {
		window.location.hash = '/notifications';
	}
</script>

<nav class="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
	<div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
		<div class="flex justify-between h-14">
			<!-- Left: Logo and Navigation -->
			<div class="flex">
				<!-- Logo -->
			<div 
  role="button"
  tabindex="0"
  onclick={() => window.location.hash = '#/'}
  onkeydown={(e) => {
	if (e.key === 'Enter' || e.key === ' ') {
	  e.preventDefault();
	  window.location.hash = '#/';
	}
  }}
  class="flex items-center cursor-pointer"
>
					<img src="/koru.svg" alt="Koru Logo" class="h-24 w-24 mr-2" />
					<span class="text-3xl font-bold text-gray-900 dark:text-white"> Koru</span>
				</div>

				<!-- Main Navigation -->
				<!-- {#if isAuthenticated}
					<div class="hidden sm:ml-8 sm:flex sm:space-x-4">
						<a
							href="#/"
							class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
						>
							Dashboard
						</a>
						<a
							href="#/repositories"
							class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
						>
							Repositories
						</a>
						<a
							href="#/deployments"
							class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
						>
							Deployments
						</a>
						<a
							href="#/databases"
							class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
						>
							Databases
						</a>
					</div>
				{/if} -->
			</div>

			<!-- Right: User Actions -->
			<div class="flex items-center space-x-4">
				{#if isAuthenticated}
					<!-- Search -->
					<!-- <button class="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
						<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
							/>
						</svg>
					</button> -->

					<!-- Notifications -->
					<button
						onclick={navigateToNotifications}
						aria-label="Notifications"
						class="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors relative rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
					>
						<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
							/>
						</svg>
						{#if notificationCount > 0}
							<span
								class="absolute top-0.5 right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-blue-600 rounded-full ring-2 ring-white dark:ring-gray-900"
							>
								{notificationCount > 99 ? '99+' : notificationCount}
							</span>
						{/if}
					</button>

					<!-- User Menu -->
					<div class="relative">
						<button
							onclick={() => (showUserMenu = !showUserMenu)}
							class="flex items-center space-x-2 p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
						>
							{#if avatarUrl}
								<img
									src={avatarUrl}
									alt="User avatar"
									class="w-8 h-8 rounded-full object-cover"
								/>
							{:else}
								<div
									class="w-8 h-8 rounded-full bg-indigo-600 dark:bg-indigo-500 flex items-center justify-center text-white text-sm font-semibold"
								>
									{userFirstName.charAt(0)}{userLastName.charAt(0)}
								</div>
							{/if}
						</button>

						{#if showUserMenu}
							<div
								class="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 dark:ring-gray-700 z-50"
							>
								<div class="py-1">
									<a
										href="#/profile"
										class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
									>
										Your Profile
									</a>
									<a
										href="#/stars"
										class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
									>
										Your Stars
									</a>
									
									<div class="border-t border-gray-200 dark:border-gray-700"></div>
									<button
										onclick={handleSignOut}
										class="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
									>
										Sign Out
									</button>
								</div>
							</div>
						{/if}
					</div>
				{:else}
					<a
						href="#/login"
						class="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-2 text-sm font-medium"
					>
						Sign In
					</a>
					<a
						href="#/register"
						class="bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
					>
						Sign Up
					</a>
				{/if}
			</div>
		</div>
	</div>
</nav>
